import Dropdown from '@jetbrains/ring-ui/components/dropdown/dropdown'
import {Directions} from '@jetbrains/ring-ui/components/popup/popup.consts'
import * as React from 'react'
import {useMemo} from 'react'
import type {ContentRect} from 'react-measure'
import {shallowEqual} from 'react-redux'

import Measure from '../../../../../containers/Measure'
import {useBuildIdFromPath, useQueryParam} from '../../../../../hooks/routes'
import type {BuildId} from '../../../../../types'
import {useDownloadedArtifacts} from '../../../../pages/BuildPage/DependenciesTab/ArtifactDependencies/ArtifactDependencies.utils'
import {DependenciesTypes} from '../../../../pages/BuildPage/DependenciesTab/DependenciesTab.modes'
import {getDeliveredArtifacts} from '../../../../pages/BuildPage/DependenciesTab/DependenciesTab.selectors'
import {MIN_POPUP_WIDTH} from '../../../BuildArtifacts/BuildArtifactsPopup'
import Popup from '../../../Popup/Popup.lazy'

import Files from './Files'

import styles from './DownloadedDeliveredArtifacts.css'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = {
  buildId: BuildId
}
export default function DownloadedDeliveredArtifacts({buildId}: Props) {
  const currentBuildId = useBuildIdFromPath()
  const isDownloaded = useQueryParam('type') === DependenciesTypes.DOWNLOADED_ARTIFACTS
  const downloaded = useDownloadedArtifacts(buildId, isDownloaded ? currentBuildId : null)
  const delivered = useAppSelector(
    state =>
      currentBuildId != null && !isDownloaded
        ? getDeliveredArtifacts(state, currentBuildId, buildId)
        : null,
    shallowEqual,
  )
  const files = isDownloaded ? downloaded : delivered
  // Popup can grow but cannot shrink
  const [treeMinWidth, setTreeMinWidth] = React.useReducer(
    (state: number, newWidth: number) => Math.max(state, newWidth),
    MIN_POPUP_WIDTH,
  )
  const onResize = (rect: ContentRect) => setTreeMinWidth(rect.offset?.width ?? 0)

  const popup = useMemo(
    () => (
      <Popup
        left={-8}
        className={styles.popup}
        directions={[Directions.LEFT_BOTTOM, Directions.LEFT_TOP]}
      >
        <Measure offset onResize={onResize}>
          {({measureRef}) => (
            <div
              style={{
                minWidth: `${treeMinWidth}px`,
              }}
              ref={measureRef}
            >
              {currentBuildId != null && files != null && (
                <Files buildId={isDownloaded ? buildId : currentBuildId} paths={files} compact />
              )}
            </div>
          )}
        </Measure>
      </Popup>
    ),
    [buildId, currentBuildId, files, isDownloaded, treeMinWidth],
  )

  return currentBuildId != null && files != null ? (
    <Dropdown hoverMode anchor={isDownloaded ? 'Downloaded artifacts' : 'Delivered artifacts'}>
      {popup}
    </Dropdown>
  ) : null
}
